import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/Card';
import { BudgetProgress } from '@/components/ui/Progress';
import { cn, formatDate } from '@/lib/utils';
import { SharedBudgetBadge } from '@/components/sharing/SharingUi';
import type { BudgetSummary, BudgetStatus } from '@/types';

function isBudgetCurrent(budget: BudgetSummary): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(budget.startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(budget.endDate);
  end.setHours(0, 0, 0, 0);
  return today >= start && today <= end;
}

interface DashboardBudgetCardProps {
  budget: BudgetSummary;
  isCurrent?: boolean;
}

export function DashboardBudgetCard({ budget, isCurrent }: DashboardBudgetCardProps) {
  const navigate = useNavigate();
  const current = isCurrent ?? isBudgetCurrent(budget);

  const spent = budget.spent ?? 0;
  const directSpent = budget.directSpent ?? spent;
  const hasChildSpend = budget.hasChildren && directSpent < spent;
  const remaining = budget.remaining ?? (budget.amount - spent);
  const percentSpent =
    budget.percentSpent ?? (budget.amount > 0 ? (spent / budget.amount) * 100 : 0);
  const isCompleted = remaining === 0;
  const isOver = remaining < 0;
  const status = isOver
    ? 'OVER_BUDGET'
    : isCompleted
    ? 'ON_TRACK'
    : (budget.status ?? getStatus(percentSpent, budget.type));

  const hasChildren = budget.hasChildren;
  const childCount = budget.childCount;

  return (
    <Card
      className={cn('transition-all hover:shadow-md active:scale-[0.99]')}
      onClick={() => navigate(`/budgets/${budget.id}`)}
    >
      <CardContent className="p-0">
        {/* Header */}
        <div className="p-4 pb-2">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <span className="block font-display text-2xl font-semibold tracking-tight text-foreground truncate">
                {budget.name}
              </span>
              <div className="flex flex-wrap items-center gap-2 mt-0.5">
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                    budget.type === 'OBLIGATION'
                      ? 'bg-primary/10 text-primary'
                      : 'bg-secondary/40 text-foreground'
                  )}
                >
                  {budget.type === 'OBLIGATION' ? 'Obligation' : 'Discretionary'}
                </span>
                <SharedBudgetBadge shared={budget.shared} owned={budget.owned} />
                {hasChildren && (
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground">
                    {childCount} sub-budget{childCount !== 1 ? 's' : ''}
                  </span>
                )}
                {!current && (
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground border border-border">
                    Not Current
                  </span>
                )}
              </div>
            </div>
            <StatusBadge status={status} />
          </div>
        </div>

        {/* Progress & amounts */}
        <div className="p-3 pt-0">
          <BudgetProgress
            spent={spent}
            directSpent={hasChildSpend ? directSpent : undefined}
            budget={budget.amount}
            currency={budget.currency}
            percentSpent={percentSpent}
            remaining={remaining}
          />
          <div className="mt-2 text-xs text-muted-foreground">
            {formatDate(budget.startDate)} – {formatDate(budget.endDate)}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function getStatus(percentSpent: number, type: string): BudgetStatus {
  if (percentSpent > 100) return 'OVER_BUDGET';
  if (percentSpent === 100) return 'ON_TRACK';
  if (percentSpent >= 90) return 'NEAR_LIMIT';
  if (type === 'OBLIGATION' && percentSpent < 50) return 'AHEAD';
  return 'ON_TRACK';
}

interface StatusBadgeProps {
  status: BudgetStatus;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const config: Record<BudgetStatus, { label: string; className: string }> = {
    ON_TRACK: { label: 'On Track', className: 'bg-success/10 text-success' },
    NEAR_LIMIT: { label: 'Near Limit', className: 'bg-warning/10 text-warning' },
    OVER_BUDGET: {
      label: 'Over Budget',
      className: 'bg-destructive/10 text-destructive',
    },
    AHEAD: {
      label: 'Ahead',
      className: 'bg-success/10 text-success',
    },
  };

  const { label, className } = config[status];

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        className
      )}
    >
      {label}
    </span>
  );
}
